import dayjs from "dayjs";
import {
  cloneElement,
  CSSProperties,
  FC,
  PropsWithChildren,
  ReactElement,
  useMemo,
} from "react";
import { Dropdown, DatePicker, Divider, Select, theme } from "antd";
import { Duration, RELATIVE_PRESETS } from "src/utils/date";
import { CloseOutlined, CalendarOutlined } from "@ant-design/icons";
import { Flex } from "./Flex";
import { Hover } from "./Hover";

// Helper function to validate and format date range
export function formatDateRange(startDate?: string, endDate?: string): string {
  if (!startDate || !endDate) return "No date range selected";
  try {
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    if (!start.isValid() || !end.isValid()) return "Invalid date range";
    return `${start.format("MMMM D, YYYY")} - ${end.format("MMMM D, YYYY")}`;
  } catch {
    return "Invalid date range";
  }
}

// Helper function to get default date range
function getDefaultDateRange(): [string, string] {
  const today = dayjs();
  const weekAgo = today.subtract(7, "day");
  return [
    weekAgo.startOf("day").toISOString(),
    today.endOf("day").toISOString(),
  ];
}

// Helper function to validate date range
function validateDateRange(
  startDate?: string,
  endDate?: string
): [string, string] {
  if (!startDate || !endDate) {
    return getDefaultDateRange();
  }

  try {
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    if (!start.isValid() || !end.isValid()) {
      return getDefaultDateRange();
    }
    return [start.toISOString(), end.toISOString()];
  } catch {
    return getDefaultDateRange();
  }
}

export const DateRangeSelect: FC<{
  startDate?: string;
  endDate?: string;
  duration?: Duration;
  onChangeDuration(val: Duration): void;
  onChangeRange(vals: [string, string]): void;
  style?: CSSProperties;
  dropdownStyle?: CSSProperties;
  allowClear?: boolean;
}> = ({
  startDate,
  endDate,
  duration,
  onChangeRange,
  onChangeDuration,
  style,
  dropdownStyle,
  allowClear = true,
}) => {
  const { token } = theme.useToken();

  const value = duration || "custom";
  const options = (
    value === "custom"
      ? [
          ...RELATIVE_PRESETS,
          {
            label: formatDateRange(startDate, endDate),
            value: "custom",
          },
        ]
      : RELATIVE_PRESETS
  ).map((p) => ({
    ...p,
    label: (
      <span
        style={{
          color: token.colorTextSecondary,
          fontSize: 13,
        }}
      >
        {p.label}
      </span>
    ),
  }));

  return (
    <Select
      suffixIcon={
        <CalendarOutlined style={{ color: token.colorTextSecondary }} />
      }
      className="date-presets-dropdown"
      options={options}
      style={{
        width: "180px",
        borderRadius: token.borderRadius,
        backgroundColor: "transparent",
        ...style,
      }}
      allowClear={allowClear}
      dropdownStyle={{
        minWidth: "300px",
        ...dropdownStyle,
      }}
      value={value}
      onChange={(val) => {
        if (val === "custom") {
          return;
        }
        onChangeDuration(val as Duration);
      }}
      dropdownRender={(menu) => (
        <div
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          style={{
            minWidth: "300px",
            ...dropdownStyle,
          }}
        >
          {menu}
          <div style={{ padding: "8px" }}>
            <DatePicker.RangePicker
              allowClear={allowClear}
              allowEmpty={[false, false]}
              value={
                startDate && endDate
                  ? [dayjs(startDate), dayjs(endDate)]
                  : undefined
              }
              onChange={(val) => {
                if (val?.[0] && val?.[1]) {
                  const range: [string, string] = [
                    val[0].startOf("day").toISOString(),
                    val[1].endOf("day").toISOString(),
                  ];
                  onChangeRange(range);
                } 
              }}
              style={{ width: "100%", padding: "0 8px" }}
            />
          </div>
        </div>
      )}
    />
  );
};

export const DateRangeDropdown: FC<
  PropsWithChildren<{
    startDate?: string;
    endDate?: string;
    duration?: Duration | "";
    onChangeDuration(val: Duration): void;
    onChangeRange(vals: [string, string]): void;
    onClear?: () => void;
    style?: CSSProperties;
  }>
> = ({
  startDate,
  endDate,
  duration,
  onClear,
  onChangeRange,
  onChangeDuration,
  children,
}) => {
  const { token } = theme.useToken();

  // Validate and memoize date range
  const [validStartDate, validEndDate] = useMemo(
    () => validateDateRange(startDate, endDate),
    [startDate, endDate]
  );

  const options = RELATIVE_PRESETS.map((p) => ({
    ...p,
    label: (
      <span
        style={{
          color: token.colorTextSecondary,
          fontSize: 12,
          fontWeight: 500,
        }}
      >
        {p.label}
      </span>
    ),
  }));

  return (
    <Dropdown
      menu={{
        selectable: true,
        selectedKeys: duration ? [duration] : undefined,
        items: options.map((item) => ({
          ...item,
          key: item.value,
          selected: true,
          onClick() {
            onChangeDuration(item.value as Duration);
          },
        })),
      }}
      trigger={["click"]}
      dropdownRender={(menu) => (
        <div
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          style={{
            backgroundColor: token.colorBgElevated,
            boxShadow: token.boxShadow,
            borderRadius: token.borderRadius,
            maxWidth: 250,
          }}
        >
          {cloneElement(menu as ReactElement, {
            style: { ...(menu as ReactElement).props.style, boxShadow: "none" },
          })}
          <Divider style={{ margin: "5px 0" }} />
          <DatePicker.RangePicker
            bordered={false}
            allowClear={false}
            allowEmpty={[false, false]}
            value={[dayjs(validStartDate), dayjs(validEndDate)]}
            disabledDate={(date) => dayjs().isBefore(date, "day")}
            onChange={(val) => {
              if (val?.[0] && val?.[1]) {
                onChangeRange([
                  val[0].startOf("day").toISOString(),
                  val[1].endOf("day").toISOString(),
                ]);
              } else {
                onChangeRange(getDefaultDateRange());
              }
            }}
          />
        </div>
      )}
    >
      <Flex style={{ display: "inline-flex" }}>
        {onClear ? (
          <>
            <span>{children}</span>
            <Hover
              baseStyle={{ cursor: "pointer", color: token.colorTextTertiary }}
              hoverStyle={{ color: token.colorText }}
            >
              <span
                onClick={() => onClear()}
                style={{ marginTop: -4, paddingRight: 6 }}
              >
                <CloseOutlined style={{ fontSize: 9 }} />
              </span>
            </Hover>
          </>
        ) : (
          children
        )}
      </Flex>
    </Dropdown>
  );
};
